import { feedbackAnnotation } from '@dha-nexus/shared';

import { captureImageSource } from '../../lib/captureAssets.js';

function point(value, fallback) {
  return {
    x: Number.isFinite(Number(value?.x)) ? Number(value.x) : fallback.x,
    y: Number.isFinite(Number(value?.y)) ? Number(value.y) : fallback.y,
  };
}

function renderOverlay(snapshot) {
  const stroke = snapshot.strokeColor || '#F4B400';
  const fill = snapshot.fillColor || 'transparent';
  const bounds = snapshot.bounds;
  const rotation = snapshot.rotationDegrees
    ? `rotate(${snapshot.rotationDegrees} ${bounds.x + bounds.width / 2} ${bounds.y + bounds.height / 2})`
    : undefined;
  if (snapshot.type === 'box') {
    return <rect x={bounds.x} y={bounds.y} width={bounds.width} height={bounds.height} fill={fill} stroke={stroke} strokeWidth="4" transform={rotation} />;
  }
  if (snapshot.type === 'highlight') {
    return <rect x={bounds.x} y={bounds.y} width={bounds.width} height={bounds.height} fill={snapshot.fillColor || stroke} fillOpacity="0.28" stroke={stroke} strokeWidth="2" transform={rotation} />;
  }
  if (snapshot.type === 'arrow') {
    const start = point(snapshot.arrow?.start, { x: bounds.x, y: bounds.y });
    const end = point(snapshot.arrow?.end, { x: bounds.x + bounds.width, y: bounds.y + bounds.height });
    return <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke={stroke} strokeWidth="5" strokeLinecap="round" markerEnd="url(#feedback-preview-arrowhead)" transform={rotation} />;
  }
  if (snapshot.type === 'pin') {
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    return <g><circle cx={cx} cy={cy} r={Math.max(16, Math.min(bounds.width || 32, bounds.height || 32) / 2)} fill={stroke} stroke="#fff" strokeWidth="3" /><text x={cx} y={cy + 6} fill="#111" textAnchor="middle" fontSize="18" fontWeight="700">{snapshot.markNumber}</text></g>;
  }
  if (snapshot.type === 'text') {
    return <g transform={rotation}><rect x={bounds.x} y={bounds.y} width={Math.max(bounds.width, 120)} height={Math.max(bounds.height, 38)} rx="4" fill={snapshot.backgroundMode === 'transparent' ? 'transparent' : snapshot.fillColor || '#ffffff'} stroke={stroke} strokeWidth="2" /><text x={bounds.x + 8} y={bounds.y + 24} fill={snapshot.textColor || stroke} fontSize="18">{snapshot.text || ''}</text></g>;
  }
  return snapshot.freehandPoints?.length ? <polyline points={snapshot.freehandPoints.map(([x, y]) => `${x},${y}`).join(' ')} fill="none" stroke={stroke} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" /> : null;
}

export function GovernedAnnotationPreview({ screenContext, alt, environmentId }) {
  const snapshot = screenContext?.annotationSnapshot;
  const available = feedbackAnnotation.isAnnotationSnapshot(snapshot);
  const imageSource = captureImageSource(screenContext, { environmentId });
  if (!imageSource) return <p className="muted">No screenshot was attached to this feedback item.</p>;
  return (
    <div className="feedback-ops-preview" data-testid="feedback-annotation-preview">
      {available ? (
        <svg className="feedback-ops-preview-overlay" viewBox={`0 0 ${snapshot.imageWidth} ${snapshot.imageHeight}`} role="img" aria-label={`${alt || 'Feedback capture'}; Selected ${snapshot.type} annotation ${snapshot.markNumber}`} preserveAspectRatio="xMidYMid meet">
          <image href={imageSource} width={snapshot.imageWidth} height={snapshot.imageHeight} preserveAspectRatio="xMidYMid meet" />
          <defs><marker id="feedback-preview-arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto"><path d="M0,0 L0,6 L9,3 z" fill={snapshot.strokeColor || '#F4B400'} /></marker></defs>
          {renderOverlay(snapshot)}
        </svg>
      ) : <><img src={imageSource} alt={alt || 'Feedback capture'} className="feedback-ops-preview-image" /><p className="feedback-ops-preview-fallback">Annotation overlay is unavailable for this legacy record.</p></>}
    </div>
  );
}
